import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import {
  AdminAccessProfile,
  AdminManagerGroup,
  AdminOrganization,
  AdminRequester,
  AdminSpace,
  AdminUser,
  AvailabilityException,
  AvailabilityRule,
  BookingSettings,
  DocumentRequirement,
  ReservationPolicy,
  ServiceCatalogItem,
  SpaceAmenity,
  SpaceAvailabilityConfig,
  SpaceImage,
  SpaceRate,
  SpaceUsageNorm,
  SpaceBookingBlock,
  SpaceServiceAssignment,
  SpaceTag,
  SpaceUnit,
} from '../models/admin.models';
import { ApiCollectionResponse, ApiDataResponse, ApiMessageResponse, ApiUserResponse } from '../models/api.models';
import {
  DashboardCategoryBreakdown,
  DashboardRequester,
  DashboardSpaceRanking,
  DashboardSummary,
  DashboardTimeseriesPoint,
} from '../models/dashboard.models';
import {
  CreateReservationPayload,
  ReservationDetail,
  ReservationDocument,
  ReservationDocumentRequestItem,
  ReservationListItem,
} from '../models/reservation.models';
import { ApiService } from './api.service';
import { PublicSpace, SpaceAvailabilityApiResponse } from '../models/public-space.models';

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  private readonly api = inject(ApiService);

  accessProfile(): Observable<AdminAccessProfile> {
    return this.api.get<AdminAccessProfile>('/admin/access-profile');
  }

  organizations(params: Record<string, unknown> = {}): Observable<ApiCollectionResponse<AdminOrganization>> {
    return this.api.get<ApiCollectionResponse<AdminOrganization>>('/admin/organizations', params);
  }

  requesters(params: Record<string, unknown> = {}): Observable<ApiCollectionResponse<AdminRequester>> {
    return this.api.get<ApiCollectionResponse<AdminRequester>>('/admin/requesters', params);
  }

  createOrganization(payload: Partial<AdminOrganization>): Observable<ApiDataResponse<AdminOrganization>> {
    return this.api.post<ApiDataResponse<AdminOrganization>>('/admin/organizations', payload);
  }

  updateOrganization(id: number, payload: Partial<AdminOrganization>): Observable<ApiDataResponse<AdminOrganization>> {
    return this.api.put<ApiDataResponse<AdminOrganization>>(`/admin/organizations/${id}`, payload);
  }

  toggleOrganizationStatus(id: number): Observable<ApiDataResponse<AdminOrganization>> {
    return this.api.put<ApiDataResponse<AdminOrganization>>(`/admin/organizations/toggle-status/${id}`, {});
  }

  myOrganization(params: Record<string, unknown> = {}): Observable<ApiDataResponse<AdminOrganization>> {
    return this.api.get<ApiDataResponse<AdminOrganization>>('/admin/organization', params);
  }

  managerGroups(params: Record<string, unknown> = {}): Observable<ApiCollectionResponse<AdminManagerGroup>> {
    return this.api.get<ApiCollectionResponse<AdminManagerGroup>>('/admin/manager-groups', params);
  }

  createManagerGroup(payload: Partial<AdminManagerGroup>): Observable<ApiDataResponse<AdminManagerGroup>> {
    return this.api.post<ApiDataResponse<AdminManagerGroup>>('/admin/manager-groups', payload);
  }

  managerGroup(id: number): Observable<ApiDataResponse<AdminManagerGroup>> {
    return this.api.get<ApiDataResponse<AdminManagerGroup>>(`/admin/manager-groups/${id}`);
  }

  updateManagerGroup(id: number, payload: Partial<AdminManagerGroup>): Observable<ApiDataResponse<AdminManagerGroup>> {
    return this.api.put<ApiDataResponse<AdminManagerGroup>>(`/admin/manager-groups/${id}`, payload);
  }

  deleteManagerGroup(id: number): Observable<ApiMessageResponse> {
    return this.api.delete<ApiMessageResponse>(`/admin/manager-groups/${id}`);
  }

  addUsersToManagerGroup(id: number, userIds: number[]): Observable<ApiMessageResponse> {
    return this.api.post<ApiMessageResponse>(`/admin/manager-groups/${id}/users`, { user_ids: userIds });
  }

  removeUsersFromManagerGroup(id: number, userIds: number[]): Observable<ApiMessageResponse> {
    return this.api.delete<ApiMessageResponse>(`/admin/manager-groups/${id}/users`, { user_ids: userIds });
  }

  users(params: Record<string, unknown> = {}): Observable<ApiCollectionResponse<AdminUser>> {
    return this.api.get<ApiCollectionResponse<AdminUser>>('/admin/users', params);
  }

  createUser(payload: Record<string, unknown>): Observable<ApiUserResponse<AdminUser>> {
    return this.api.post<ApiUserResponse<AdminUser>>('/admin/users', payload);
  }

  user(id: number): Observable<ApiUserResponse<AdminUser>> {
    return this.api.get<ApiUserResponse<AdminUser>>(`/admin/users/${id}`);
  }

  updateUser(id: number, payload: Record<string, unknown>): Observable<ApiUserResponse<AdminUser>> {
    return this.api.put<ApiUserResponse<AdminUser>>(`/admin/users/${id}`, payload);
  }

  toggleUserStatus(id: number): Observable<ApiUserResponse<AdminUser>> {
    return this.api.put<ApiUserResponse<AdminUser>>(`/admin/users/toggle-status/${id}`, {});
  }

  resendInvitation(id: number): Observable<ApiMessageResponse> {
    return this.api.post<ApiMessageResponse>(`/admin/users/resend-invitation/${id}`, {});
  }

  spaces(params: Record<string, unknown> = {}): Observable<ApiCollectionResponse<AdminSpace>> {
    return this.api.get<ApiCollectionResponse<AdminSpace>>('/admin/spaces', params);
  }

  createSpace(payload: Record<string, unknown>): Observable<ApiDataResponse<AdminSpace>> {
    return this.api.post<ApiDataResponse<AdminSpace>>('/admin/spaces', payload);
  }

  space(id: number): Observable<ApiDataResponse<AdminSpace>> {
    return this.api.get<ApiDataResponse<AdminSpace>>(`/admin/spaces/${id}`);
  }

  updateSpace(id: number, payload: Record<string, unknown>): Observable<ApiDataResponse<AdminSpace>> {
    return this.api.put<ApiDataResponse<AdminSpace>>(`/admin/spaces/${id}`, payload);
  }

  deleteSpace(id: number): Observable<ApiMessageResponse> {
    return this.api.delete<ApiMessageResponse>(`/admin/spaces/${id}`);
  }

  uploadSpaceImage(id: number, file: File): Observable<ApiDataResponse<AdminSpace>> {
    const formData = new FormData();
    formData.append('image', file);
    return this.api.post<ApiDataResponse<AdminSpace>>(`/admin/spaces/${id}/image`, formData);
  }

  spaceAvailability(id: number): Observable<SpaceAvailabilityConfig> {
    return this.api.get<SpaceAvailabilityConfig>(`/admin/spaces/${id}/availability`);
  }

  createAvailabilityRule(id: number, payload: Partial<AvailabilityRule>): Observable<ApiDataResponse<AvailabilityRule>> {
    return this.api.post<ApiDataResponse<AvailabilityRule>>(`/admin/spaces/${id}/availability/rules`, payload);
  }

  createAvailabilityException(id: number, payload: Partial<AvailabilityException>): Observable<ApiDataResponse<AvailabilityException>> {
    return this.api.post<ApiDataResponse<AvailabilityException>>(`/admin/spaces/${id}/availability/exceptions`, payload);
  }

  deleteAvailabilityRule(id: number, ruleId: number): Observable<ApiMessageResponse> {
    return this.api.delete<ApiMessageResponse>(`/admin/spaces/${id}/availability/rules/${ruleId}`);
  }

  deleteAvailabilityException(id: number, exceptionId: number): Observable<ApiMessageResponse> {
    return this.api.delete<ApiMessageResponse>(`/admin/spaces/${id}/availability/exceptions/${exceptionId}`);
  }

  bookingSettings(id: number): Observable<BookingSettings> {
    return this.api.get<BookingSettings>(`/admin/spaces/${id}/booking-settings`);
  }

  createBookingSettings(id: number, payload: BookingSettings): Observable<BookingSettings> {
    return this.api.post<BookingSettings>(`/admin/spaces/${id}/booking-settings`, payload);
  }

  updateBookingSettings(id: number, payload: BookingSettings): Observable<BookingSettings> {
    return this.api.put<BookingSettings>(`/admin/spaces/${id}/booking-settings`, payload);
  }

  reservationPolicy(id: number): Observable<ReservationPolicy> {
    return this.api.get<ReservationPolicy>(`/admin/spaces/${id}/reservation-policy`);
  }

  createReservationPolicy(id: number, payload: ReservationPolicy): Observable<ReservationPolicy> {
    return this.api.post<ReservationPolicy>(`/admin/spaces/${id}/reservation-policy`, payload);
  }

  updateReservationPolicy(id: number, payload: ReservationPolicy): Observable<ReservationPolicy> {
    return this.api.put<ReservationPolicy>(`/admin/spaces/${id}/reservation-policy`, payload);
  }

  documentRequirements(id: number): Observable<DocumentRequirement[]> {
    return this.api.get<DocumentRequirement[]>(`/admin/spaces/${id}/document-requirements`);
  }

  createDocumentRequirement(id: number, payload: Partial<DocumentRequirement>): Observable<DocumentRequirement> {
    return this.api.post<DocumentRequirement>(`/admin/spaces/${id}/document-requirements`, payload);
  }

  deleteDocumentRequirement(id: number, requirementId: number): Observable<ApiMessageResponse> {
    return this.api.delete<ApiMessageResponse>(`/admin/spaces/${id}/document-requirements/${requirementId}`);
  }

  spaceServices(id: number): Observable<SpaceServiceAssignment[]> {
    return this.api.get<SpaceServiceAssignment[]>(`/admin/spaces/${id}/services`);
  }

  assignServiceToSpace(id: number, payload: Record<string, unknown>): Observable<SpaceServiceAssignment> {
    return this.api.post<SpaceServiceAssignment>(`/admin/spaces/${id}/services`, payload);
  }

  updateSpaceService(id: number, serviceId: number, payload: Record<string, unknown>): Observable<SpaceServiceAssignment> {
    return this.api.put<SpaceServiceAssignment>(`/admin/spaces/${id}/services/${serviceId}`, payload);
  }

  removeSpaceService(id: number, serviceId: number): Observable<ApiMessageResponse> {
    return this.api.delete<ApiMessageResponse>(`/admin/spaces/${id}/services/${serviceId}`);
  }

  spaceTags(id: number): Observable<ApiCollectionResponse<SpaceTag>> {
    return this.api.get<ApiCollectionResponse<SpaceTag>>(`/admin/spaces/${id}/tags`);
  }

  syncSpaceTags(id: number, tagIds: number[]): Observable<ApiCollectionResponse<SpaceTag>> {
    return this.api.put<ApiCollectionResponse<SpaceTag>>(`/admin/spaces/${id}/tags`, { tag_ids: tagIds });
  }

  tags(params: Record<string, unknown> = {}): Observable<ApiCollectionResponse<SpaceTag>> {
    return this.api.get<ApiCollectionResponse<SpaceTag>>('/admin/tags', params);
  }

  createTag(payload: Partial<SpaceTag>): Observable<SpaceTag> {
    return this.api.post<SpaceTag>('/admin/tags', payload);
  }

  deleteTag(id: number): Observable<ApiMessageResponse> {
    return this.api.delete<ApiMessageResponse>(`/admin/tags/${id}`);
  }

  spaceAmenities(id: number): Observable<ApiCollectionResponse<SpaceAmenity>> {
    return this.api.get<ApiCollectionResponse<SpaceAmenity>>(`/admin/spaces/${id}/amenities`);
  }

  syncSpaceAmenities(id: number, amenityIds: number[]): Observable<ApiCollectionResponse<SpaceAmenity>> {
    return this.api.put<ApiCollectionResponse<SpaceAmenity>>(`/admin/spaces/${id}/amenities`, { amenity_ids: amenityIds });
  }

  amenities(params: Record<string, unknown> = {}): Observable<ApiCollectionResponse<SpaceAmenity>> {
    return this.api.get<ApiCollectionResponse<SpaceAmenity>>('/admin/amenities', params);
  }

  createAmenity(payload: Partial<SpaceAmenity>): Observable<SpaceAmenity> {
    return this.api.post<SpaceAmenity>('/admin/amenities', payload);
  }

  deleteAmenity(id: number): Observable<ApiMessageResponse> {
    return this.api.delete<ApiMessageResponse>(`/admin/amenities/${id}`);
  }

  spaceRates(id: number): Observable<ApiCollectionResponse<SpaceRate>> {
    return this.api.get<ApiCollectionResponse<SpaceRate>>(`/admin/spaces/${id}/rates`);
  }

  createSpaceRate(id: number, payload: Partial<SpaceRate>): Observable<SpaceRate> {
    return this.api.post<SpaceRate>(`/admin/spaces/${id}/rates`, payload);
  }

  updateSpaceRate(id: number, rateId: number, payload: Partial<SpaceRate>): Observable<SpaceRate> {
    return this.api.put<SpaceRate>(`/admin/spaces/${id}/rates/${rateId}`, payload);
  }

  deleteSpaceRate(id: number, rateId: number): Observable<ApiMessageResponse> {
    return this.api.delete<ApiMessageResponse>(`/admin/spaces/${id}/rates/${rateId}`);
  }

  spaceUsageNorms(id: number): Observable<ApiCollectionResponse<SpaceUsageNorm>> {
    return this.api.get<ApiCollectionResponse<SpaceUsageNorm>>(`/admin/spaces/${id}/usage-norms`);
  }

  createSpaceUsageNorm(id: number, payload: Partial<SpaceUsageNorm>): Observable<SpaceUsageNorm> {
    return this.api.post<SpaceUsageNorm>(`/admin/spaces/${id}/usage-norms`, payload);
  }

  updateSpaceUsageNorm(id: number, normId: number, payload: Partial<SpaceUsageNorm>): Observable<SpaceUsageNorm> {
    return this.api.put<SpaceUsageNorm>(`/admin/spaces/${id}/usage-norms/${normId}`, payload);
  }

  deleteSpaceUsageNorm(id: number, normId: number): Observable<ApiMessageResponse> {
    return this.api.delete<ApiMessageResponse>(`/admin/spaces/${id}/usage-norms/${normId}`);
  }

  bookingBlocks(id: number): Observable<ApiCollectionResponse<SpaceBookingBlock>> {
    return this.api.get<ApiCollectionResponse<SpaceBookingBlock>>(`/admin/spaces/${id}/booking-blocks`);
  }

  createBookingBlock(id: number, payload: Partial<SpaceBookingBlock>): Observable<SpaceBookingBlock> {
    return this.api.post<SpaceBookingBlock>(`/admin/spaces/${id}/booking-blocks`, payload);
  }

  deleteBookingBlock(id: number, blockId: number): Observable<ApiMessageResponse> {
    return this.api.delete<ApiMessageResponse>(`/admin/spaces/${id}/booking-blocks/${blockId}`);
  }

  spaceImages(id: number): Observable<ApiCollectionResponse<SpaceImage>> {
    return this.api.get<ApiCollectionResponse<SpaceImage>>(`/admin/spaces/${id}/images`);
  }

  uploadSpaceGalleryImage(id: number, file: File): Observable<SpaceImage> {
    const formData = new FormData();
    formData.append('image', file);
    return this.api.post<SpaceImage>(`/admin/spaces/${id}/images`, formData);
  }

  deleteSpaceImage(id: number, imageId: number): Observable<ApiMessageResponse> {
    return this.api.delete<ApiMessageResponse>(`/admin/spaces/${id}/images/${imageId}`);
  }

  spaceUnits(id: number): Observable<ApiCollectionResponse<SpaceUnit>> {
    return this.api.get<ApiCollectionResponse<SpaceUnit>>(`/admin/spaces/${id}/units`);
  }

  createSpaceUnit(id: number, payload: Partial<SpaceUnit>): Observable<SpaceUnit> {
    return this.api.post<SpaceUnit>(`/admin/spaces/${id}/units`, payload);
  }

  updateSpaceUnit(id: number, unitId: number, payload: Partial<SpaceUnit>): Observable<SpaceUnit> {
    return this.api.put<SpaceUnit>(`/admin/spaces/${id}/units/${unitId}`, payload);
  }

  deleteSpaceUnit(id: number, unitId: number): Observable<ApiMessageResponse> {
    return this.api.delete<ApiMessageResponse>(`/admin/spaces/${id}/units/${unitId}`);
  }

  services(params: Record<string, unknown> = {}): Observable<ApiCollectionResponse<ServiceCatalogItem>> {
    return this.api.get<ApiCollectionResponse<ServiceCatalogItem>>('/admin/services', params);
  }

  createService(payload: Partial<ServiceCatalogItem>): Observable<ApiDataResponse<ServiceCatalogItem>> {
    return this.api.post<ApiDataResponse<ServiceCatalogItem>>('/admin/services', payload);
  }

  updateService(id: number, payload: Partial<ServiceCatalogItem>): Observable<ApiDataResponse<ServiceCatalogItem>> {
    return this.api.put<ApiDataResponse<ServiceCatalogItem>>(`/admin/services/${id}`, payload);
  }

  deleteService(id: number): Observable<ApiMessageResponse> {
    return this.api.delete<ApiMessageResponse>(`/admin/services/${id}`);
  }

  dashboardSummary(
    params: { from: string; to: string; organization_id?: number },
  ): Observable<ApiDataResponse<DashboardSummary>> {
    return this.api.get<ApiDataResponse<DashboardSummary>>('/admin/dashboard/summary', params);
  }

  dashboardReservationsTimeseries(
    params: { from: string; to: string; organization_id?: number },
  ): Observable<ApiDataResponse<DashboardTimeseriesPoint[]>> {
    return this.api.get<ApiDataResponse<DashboardTimeseriesPoint[]>>('/admin/dashboard/reservations-timeseries', params);
  }

  dashboardReservationsBySpace(
    params: { from: string; to: string; limit?: number; organization_id?: number },
  ): Observable<ApiDataResponse<DashboardSpaceRanking[]>> {
    return this.api.get<ApiDataResponse<DashboardSpaceRanking[]>>('/admin/dashboard/reservations-by-space', params);
  }

  dashboardTopRequesters(
    params: { from: string; to: string; limit?: number; organization_id?: number },
  ): Observable<ApiDataResponse<DashboardRequester[]>> {
    return this.api.get<ApiDataResponse<DashboardRequester[]>>('/admin/dashboard/top-requesters', params);
  }

  dashboardSpaceCategories(
    params: { from: string; to: string; organization_id?: number },
  ): Observable<ApiDataResponse<DashboardCategoryBreakdown[]>> {
    return this.api.get<ApiDataResponse<DashboardCategoryBreakdown[]>>('/admin/dashboard/space-categories', params);
  }

  bookableSpaces(params: Record<string, unknown> = {}): Observable<ApiCollectionResponse<PublicSpace>> {
    return this.api.get<ApiCollectionResponse<PublicSpace>>('/admin/reservations/bookable-spaces', params);
  }

  reservationSlots(spaceId: number, date: string, unitId: number | null): Observable<SpaceAvailabilityApiResponse> {
    return this.api.get<SpaceAvailabilityApiResponse>(`/admin/reservations/bookable-spaces/${spaceId}/available-slots`, {
      date, ...(unitId ? { unit_id: unitId } : {}),
    });
  }

  /** Days in the range that already have an active reservation (calendar dot). */
  reservationReservedDates(spaceId: number, from: string, to: string, unitId: number | null): Observable<ApiDataResponse<string[]>> {
    return this.api.get<ApiDataResponse<string[]>>(`/admin/reservations/bookable-spaces/${spaceId}/reserved-dates`, {
      from, to, ...(unitId ? { unit_id: unitId } : {}),
    });
  }

  /** Days in the range with no bookable slot at all (closed, fully booked, outside the schedule). */
  reservationUnavailableDates(spaceId: number, from: string, to: string, unitId: number | null): Observable<ApiDataResponse<string[]>> {
    return this.api.get<ApiDataResponse<string[]>>(`/admin/reservations/bookable-spaces/${spaceId}/unavailable-dates`, {
      from, to, ...(unitId ? { unit_id: unitId } : {}),
    });
  }

  createReservation(payload: CreateReservationPayload & { organization_id?: number }): Observable<ReservationDetail> {
    return this.api.post<ReservationDetail>('/admin/reservations', payload);
  }

  reservations(params: Record<string, unknown> = {}): Observable<ApiCollectionResponse<ReservationListItem>> {
    return this.api.get<ApiCollectionResponse<ReservationListItem>>('/admin/reservations', params);
  }

  reservation(id: number): Observable<ReservationDetail> {
    return this.api.get<ReservationDetail>(`/admin/reservations/${id}`);
  }

  confirmReservation(id: number, comment?: string): Observable<ReservationDetail> {
    return this.api.post<ReservationDetail>(`/admin/reservations/${id}/confirm`, comment ? { comment } : {});
  }

  rejectReservation(id: number, comment?: string): Observable<ReservationDetail> {
    return this.api.post<ReservationDetail>(`/admin/reservations/${id}/reject`, comment ? { comment } : {});
  }

  cancelReservation(id: number, comment: string): Observable<ReservationDetail> {
    return this.api.post<ReservationDetail>(`/admin/reservations/${id}/cancel`, { comment });
  }

  cancelReservationDate(reservationId: number, dateId: number, comment: string): Observable<ReservationDetail> {
    return this.api.post<ReservationDetail>(`/admin/reservations/${reservationId}/dates/${dateId}/cancel`, { comment });
  }

  addReservationNote(id: number, comment: string): Observable<ReservationDetail> {
    return this.api.post<ReservationDetail>(`/admin/reservations/${id}/notes`, { comment });
  }

  sendReservationMessage(id: number, comment: string): Observable<ReservationDetail> {
    return this.api.post<ReservationDetail>(`/admin/reservations/${id}/messages`, { comment });
  }

  reservationDocuments(id: number): Observable<ReservationDocument[]> {
    return this.api.get<ReservationDocument[]>(`/admin/reservations/${id}/documents`);
  }

  reservationDocumentRequests(id: number): Observable<ReservationDocumentRequestItem[]> {
    return this.api.get<ReservationDocumentRequestItem[]>(`/admin/reservations/${id}/documents/requests`);
  }

  requestReservationDocument(
    reservationId: number,
    name: string,
    fileCategory: 'image' | 'pdf' | 'other' | 'all',
    comment: string,
  ): Observable<ReservationDocumentRequestItem> {
    return this.api.post<ReservationDocumentRequestItem>(`/admin/reservations/${reservationId}/documents/request`, {
      name,
      file_category: fileCategory,
      comment,
    });
  }

  approveDocument(
    reservationId: number,
    documentId: number,
    reviewComment?: string,
    internalNote?: string,
  ): Observable<ReservationDocument> {
    return this.api.post<ReservationDocument>(`/admin/reservations/${reservationId}/documents/${documentId}/approve`, {
      ...(reviewComment ? { review_comment: reviewComment } : {}),
      ...(internalNote ? { internal_note: internalNote } : {}),
    });
  }

  rejectDocument(
    reservationId: number,
    documentId: number,
    reviewComment: string,
    internalNote?: string,
  ): Observable<ReservationDocument> {
    return this.api.post<ReservationDocument>(`/admin/reservations/${reservationId}/documents/${documentId}/reject`, {
      review_comment: reviewComment,
      ...(internalNote ? { internal_note: internalNote } : {}),
    });
  }

  downloadAdminDocument(reservationId: number, documentId: number): Observable<Blob> {
    return this.api.download(`/admin/reservations/${reservationId}/documents/${documentId}/download`);
  }
}
